/**
 * Skills graph — Layer 2 output mapped onto Layer 4 open standards.
 *
 * Every row shows the level, the confidence, the evidence lines it rests on,
 * and how the skill is anchored (ESCO concept, ISCO-08 prior, or honestly
 * "not mapped yet"). We never present an unmapped skill as standards-backed.
 */

import { useMemo, useState } from "react";
import { ChevronDown, ExternalLink, Loader2 } from "lucide-react";
import type { InferredSkill, SkillLevel } from "@wcc360/engine/skills-engine";

import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";

const LEVEL_COPY: Record<SkillLevel, string> = {
  aware: "Aware",
  novice: "Getting started",
  intermediate: "Doing it",
  advanced: "Strong",
  expert: "Deep",
};

const PROVENANCE_COPY: Record<string, string> = {
  esco_exact: "Matched to ESCO",
  esco_derived: "Derived from ESCO",
  isco_prior: "ISCO-08 prior",
  onet_prior: "O*NET prior",
  unmapped: "Not mapped yet",
};

export interface SkillsGraphPanelProps {
  skills: InferredSkill[];
  loading?: boolean;
  /** Cap the visible rows; the rest sit behind "show all". */
  initialCount?: number;
}

export function SkillsGraphPanel({ skills, loading, initialCount = 10 }: SkillsGraphPanelProps) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const ordered = useMemo(
    () => [...skills].sort((a, b) => b.level_score - a.level_score || a.label.localeCompare(b.label)),
    [skills],
  );
  const visible = showAll ? ordered : ordered.slice(0, initialCount);

  return (
    <section aria-labelledby="skills-graph-heading" className="space-y-5">
      <div>
        <div className="text-[11px] font-display uppercase tracking-[0.35em] text-primary/80">
          Layer 4 · Open standards mapping
        </div>
        <h2 id="skills-graph-heading" className="mt-3 font-display text-2xl sm:text-3xl font-bold">
          Your skills, in the world's language
        </h2>
        <p className="mt-2 text-muted-foreground max-w-2xl">
          Each one is anchored to ESCO — the free, public European skills taxonomy — so employers
          and courses recognise it without translation.
        </p>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Building your graph…
        </div>
      )}

      {!loading && !ordered.length && (
        <div className="rounded-3xl border border-border bg-card/60 p-6 text-sm text-muted-foreground">
          Your graph fills in as you answer evidence questions below. One tick is enough to start.
        </div>
      )}

      <ul className="space-y-2">
        {visible.map((s) => {
          const open = expanded === s.skill_id;
          return (
            <li key={s.skill_id} className="rounded-3xl border border-border bg-card/60 overflow-hidden">
              <button
                type="button"
                onClick={() => setExpanded(open ? null : s.skill_id)}
                aria-expanded={open}
                className="w-full text-left p-4 sm:p-5 min-h-[64px] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{s.label}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <Badge variant="secondary" className="text-[10px]">{LEVEL_COPY[s.level]}</Badge>
                      <span>Confidence {Math.round(s.confidence * 100)}%</span>
                      <span aria-hidden>·</span>
                      <span>{PROVENANCE_COPY[s.taxonomy?.provenance ?? "unmapped"]}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="w-20 sm:w-28">
                      <Progress value={s.level_score} className="h-2" aria-label={`${s.label} evidence strength`} />
                    </div>
                    <ChevronDown
                      className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
                      aria-hidden
                    />
                  </div>
                </div>
              </button>

              {open && (
                <div className="border-t border-border/60 px-4 sm:px-5 py-4 space-y-3">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">
                    What this rests on
                  </div>
                  <ul className="space-y-1.5 text-sm">
                    {s.evidence.map((line, i) => (
                      <li key={i} className="text-foreground/90">— {line}</li>
                    ))}
                  </ul>
                  {s.taxonomy?.esco_uri && (
                    <a
                      href={s.taxonomy.esco_uri}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline min-h-[44px]"
                    >
                      View the ESCO definition <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                    </a>
                  )}
                  {s.taxonomy?.isco_code && (
                    <div className="text-xs text-muted-foreground">ISCO-08 {s.taxonomy.isco_code}</div>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {ordered.length > initialCount && (
        <Button variant="ghost" size="sm" onClick={() => setShowAll((v) => !v)}>
          {showAll ? "Show fewer" : `Show all ${ordered.length} skills`}
        </Button>
      )}
    </section>
  );
}
